import { z } from 'zod';
import { supabase } from '@/shared/supabase/client';
import type { DepotRelances, DepotSav } from '../types';
import { parLots, selonIds } from '../lectures';
import { cheminPreuveSav } from './chemins';
import {
  COLONNES_HISTORIQUE,
  ligneHistorique,
  messageRefus,
  versEntreeHistorique,
} from './lignes-chantiers';
import {
  COLONNES_RELANCE,
  SELECTION_FICHE_TICKET,
  SELECTION_RESUME_TICKET,
  SELECTION_TICKET,
  filtreHistoriqueTicket,
  ligneDeSaisieTicket,
  ligneFicheTicket,
  ligneRelance,
  ligneResumeTicket,
  ligneStatutTicket,
  ligneTicket,
  versFicheTicket,
  versRelance,
  versResumeTicket,
  versTicket,
} from './lignes-sav';
import { adresseSignee, deposerPuisEnregistrer, retirerFichiers } from './stockage';

const lignesId = z.array(z.object({ id: z.string() }));
const lignesChemin = z.array(z.object({ chemin: z.string() }));

/* Le chantier supprimé entre-temps : la clé étrangère refuse. */
const SAISIE = { '23503': 'Ce chantier n’existe plus.' };

const TAILLE_LOT = 100;

/**
 * Les gestes sur un ticket passent par les fonctions de db/007-sav.sql : la
 * base tranche, avec les messages de sav.ts.
 */
async function action(
  fonction:
    'affecter_ticket' | 'demarrer_ticket' | 'resoudre_ticket' | 'clore_ticket' | 'rouvrir_ticket',
  args: Record<string, string | null>,
  defaut: string
): Promise<void> {
  const { error } = await supabase().rpc(fonction, args);
  if (error) throw new Error(messageRefus(error, defaut));
}

export const savSupabase: DepotSav = {
  async lister() {
    const { data, error } = await supabase()
      .from('tickets_sav')
      .select(SELECTION_RESUME_TICKET)
      .order('ouvert_le', { ascending: false });
    if (error) throw new Error(messageRefus(error, 'Les tickets SAV n’ont pas pu être lus.'));
    return z.array(ligneResumeTicket).parse(data).map(versResumeTicket);
  },

  async lire(id) {
    const { data, error } = await supabase()
      .from('tickets_sav')
      .select(SELECTION_FICHE_TICKET)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(messageRefus(error, 'Le ticket n’a pas pu être lu.'));
    if (data === null) return null;
    return versFicheTicket(ligneFicheTicket.parse(data));
  },

  async fiches(ids) {
    const uniques = [...new Set(ids)];
    /* Par paquets : une liste `in.(…)` trop longue dépasse ce qu'une adresse porte. */
    const lots = await Promise.all(
      parLots(uniques, TAILLE_LOT).map((lot) =>
        supabase().from('tickets_sav').select(SELECTION_FICHE_TICKET).in('id', lot)
      )
    );
    const fiches = lots.flatMap(({ data, error }) => {
      if (error) throw new Error(messageRefus(error, 'Les tickets n’ont pas pu être lus.'));
      return z
        .array(ligneFicheTicket)
        .parse(data)
        .map(versFicheTicket)
        .filter((f) => f !== null);
    });
    return selonIds(uniques, fiches);
  },

  async signaler(s) {
    /* La base vérifie que le chantier est le sien, pose la référence et le lui confie (db/033). */
    const reponse = await supabase().rpc('signaler_sav', {
      p_chantier: s.chantierId,
      p_systeme: s.systeme,
      p_piece: s.piece.trim(),
      p_commentaire: s.commentaire.trim(),
      p_marque_modele: s.marqueModele.trim() || null,
      p_numero_serie: s.numeroSerie.trim() || null,
      p_etat_piece: s.etatPiece,
      p_constate_le: s.constateLe || null,
      p_arret: s.arret,
    });
    if (reponse.error) {
      throw new Error(messageRefus(reponse.error, 'Le ticket n’a pas pu être envoyé.'));
    }
    const id: unknown = reponse.data;
    if (typeof id !== 'string') throw new Error('Le ticket n’a pas pu être envoyé.');
    return id;
  },

  async creer(saisie) {
    /* La référence, l'auteur et le statut sont posés par la base. */
    const { data, error } = await supabase()
      .from('tickets_sav')
      .insert(ligneDeSaisieTicket(saisie))
      .select(SELECTION_TICKET)
      .single();
    if (error) throw new Error(messageRefus(error, 'Le ticket n’a pas pu être créé.', SAISIE));
    return versTicket(ligneTicket.parse(data));
  },

  async modifier(id, saisie) {
    const { data, error } = await supabase()
      .from('tickets_sav')
      .update(ligneDeSaisieTicket(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'Le ticket n’a pas pu être modifié.', SAISIE));
    if (lignesId.parse(data).length === 0) throw new Error('Ticket introuvable.');
  },

  async supprimer(id) {
    /* Les preuves partent avec le ticket (cascade), pas leurs fichiers. */
    const preuves = await supabase().from('preuves_sav').select('chemin').eq('ticket_id', id);
    if (preuves.error) {
      throw new Error(messageRefus(preuves.error, 'Le ticket n’a pas pu être supprimé.'));
    }
    const chemins = lignesChemin.parse(preuves.data).map((p) => p.chemin);
    const { data, error } = await supabase().from('tickets_sav').delete().eq('id', id).select('id');
    if (error) throw new Error(messageRefus(error, 'Le ticket n’a pas pu être supprimé.'));
    if (lignesId.parse(data).length === 0) throw new Error('Ticket introuvable.');
    await retirerFichiers(chemins);
  },

  affecter(id, affectation) {
    return action(
      'affecter_ticket',
      {
        p_ticket: id,
        p_partenaire: affectation.partenaireId,
        p_debut: affectation.debutPrevu,
        p_fin: affectation.finPrevue,
      },
      'Le ticket n’a pas pu être affecté.'
    );
  },

  demarrer(id) {
    return action('demarrer_ticket', { p_ticket: id }, 'L’intervention n’a pas pu être démarrée.');
  },

  resoudre(id, rapport) {
    return action(
      'resoudre_ticket',
      { p_ticket: id, p_rapport: rapport.trim() },
      'Le ticket n’a pas pu être résolu.'
    );
  },

  clore(id) {
    return action('clore_ticket', { p_ticket: id }, 'Le ticket n’a pas pu être clos.');
  },

  rouvrir(id, motif) {
    return action(
      'rouvrir_ticket',
      { p_ticket: id, p_motif: motif.trim() },
      'Le ticket n’a pas pu être rouvert.'
    );
  },

  async ajouterPreuve(id, fichier, type) {
    /* Le statut évite d'envoyer un fichier que la base refuserait ensuite
       (proteger_preuves_sav) ; la base reste juge. */
    const ticket = await supabase().from('tickets_sav').select('statut').eq('id', id).maybeSingle();
    if (ticket.error) {
      throw new Error(messageRefus(ticket.error, 'La preuve n’a pas pu être enregistrée.'));
    }
    if (ticket.data === null) throw new Error('Ticket introuvable.');
    const { statut } = ligneStatutTicket.parse(ticket.data);
    if (statut === 'resolu' || statut === 'clos') {
      throw new Error('Ce ticket est résolu : rouvrez-le pour ajouter une preuve.');
    }
    const chemin = cheminPreuveSav(id, fichier.name, crypto.randomUUID());
    await deposerPuisEnregistrer(chemin, fichier, () =>
      supabase().from('preuves_sav').insert({
        ticket_id: id,
        type,
        nom: fichier.name,
        taille: fichier.size,
        type_mime: fichier.type,
        chemin,
      })
    );
  },

  async supprimerPreuve(preuveId) {
    const { data, error } = await supabase()
      .from('preuves_sav')
      .delete()
      .eq('id', preuveId)
      .select('chemin');
    if (error) throw new Error(messageRefus(error, 'La preuve n’a pas pu être supprimée.'));
    const chemins = lignesChemin.parse(data).map((p) => p.chemin);
    if (chemins.length === 0) {
      throw new Error('Cette preuve n’existe plus, ou elle a été déposée par quelqu’un d’autre.');
    }
    await retirerFichiers(chemins);
  },

  adressePreuve(chemin) {
    return adresseSignee(chemin);
  },

  async historique(id) {
    const { data, error } = await supabase()
      .from('journal')
      .select(COLONNES_HISTORIQUE)
      .or(filtreHistoriqueTicket(id))
      .order('horodatage', { ascending: false })
      .order('id', { ascending: false })
      .limit(100);
    if (error) throw new Error(messageRefus(error, 'L’historique n’a pas pu être lu.'));
    return z.array(ligneHistorique).parse(data).map(versEntreeHistorique);
  },
};

export const relancesSupabase: DepotRelances = {
  async lister() {
    const { data, error } = await supabase()
      .from('relances')
      .select(COLONNES_RELANCE)
      .order('envoyee_le', { ascending: false })
      .limit(500);
    if (error) throw new Error(messageRefus(error, 'Les relances n’ont pas pu être lues.'));
    return z.array(ligneRelance).parse(data).map(versRelance);
  },

  async noter(partenaireId, sujet) {
    /* La date et l'auteur sont posés par la base. */
    const { error } = await supabase()
      .from('relances')
      .insert({ partenaire_id: partenaireId, sujet });
    if (error) throw new Error(messageRefus(error, 'La relance n’a pas pu être notée.'));
  },
};
